(() => {
  const liveRegion = () => document.getElementById('RhiaLiveRegion');

  function initPageEntrance() {
    if (document.body.classList.contains('is-page-ready')) return;
    if (!document.documentElement.classList.contains('motion-ready')) {
      document.body.classList.add('is-page-ready');
      return;
    }
    requestAnimationFrame(() => requestAnimationFrame(() => {
      document.body.classList.add('is-page-ready');
    }));
  }

  function initMenu() {
    const toggle = document.querySelector('[data-menu-toggle]');
    const menu = document.querySelector('[data-mobile-menu]');
    if (!toggle || !menu || toggle.dataset.ready) return;
    toggle.dataset.ready = 'true';
    const label = toggle.querySelector('.sr-only');
    let closeTimer;

    const close = (immediate = false) => {
      clearTimeout(closeTimer);
      toggle.setAttribute('aria-expanded', 'false');
      if (label) label.textContent = 'Open menu';
      menu.classList.remove('is-open');
      document.body.classList.remove('menu-open');
      const finish = () => {
        if (toggle.getAttribute('aria-expanded') === 'false') menu.hidden = true;
      };
      if (immediate) finish();
      else closeTimer = window.setTimeout(finish, 320);
    };
    const openMenu = () => {
      clearTimeout(closeTimer);
      toggle.setAttribute('aria-expanded', 'true');
      if (label) label.textContent = 'Close menu';
      menu.hidden = false;
      document.body.classList.add('menu-open');
      requestAnimationFrame(() => menu.classList.add('is-open'));
    };
    toggle.addEventListener('click', () => {
      const isOpen = toggle.getAttribute('aria-expanded') === 'true';
      if (isOpen) close();
      else openMenu();
    });
    menu.addEventListener('click', (event) => {
      if (event.target.closest('a')) close();
    });
    document.addEventListener('click', (event) => {
      if (toggle.getAttribute('aria-expanded') === 'true' && !event.target.closest('[data-site-header]')) close();
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') close();
    });
    window.addEventListener('resize', () => {
      if (window.innerWidth > 720) close(true);
    }, { passive: true });
  }

  function initReveal(root = document) {
    const items = [...root.querySelectorAll('.reveal:not([data-reveal-ready])')];
    if (!items.length) return;
    if (!('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      items.forEach((item) => {
        item.dataset.revealReady = 'true';
        item.classList.add('is-visible');
      });
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -8%' });
    const groupCounts = new Map();
    items.forEach((item) => {
      const group = item.parentElement;
      const index = groupCounts.get(group) || 0;
      groupCounts.set(group, index + 1);
      item.dataset.revealReady = 'true';
      item.style.setProperty('--reveal-delay', `${Math.min(index, 4) * 70}ms`);
      observer.observe(item);
    });
  }

  function initScrollFlow() {
    if (document.documentElement.dataset.scrollFlowReady) return;
    document.documentElement.dataset.scrollFlowReady = 'true';
    let frame;
    let isScrolled = window.scrollY > 48;
    const update = () => {
      const y = window.scrollY;
      if (!isScrolled && y > 64) isScrolled = true;
      if (isScrolled && y < 28) isScrolled = false;
      document.body.classList.toggle('is-page-scrolled', isScrolled);
      frame = null;
    };
    const requestUpdate = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate, { passive: true });
    update();
  }

  function initHow(root = document) {
    root.querySelectorAll('[data-how-it-works]:not([data-ready])').forEach((component) => {
      component.dataset.ready = 'true';
      const steps = [...component.querySelectorAll('[data-how-step]')];
      if (!steps.length) return;
      if (!('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        steps.forEach((step) => step.classList.add('is-visible'));
        return;
      }
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      }, { threshold: .16, rootMargin: '0px 0px -6%' });
      steps.forEach((step) => observer.observe(step));
    });
  }

  function initProblem(root = document) {
    root.querySelectorAll('[data-problem-explorer]:not([data-problem-ready])').forEach((component) => {
      component.dataset.problemReady = 'true';
      const tabs = [...component.querySelectorAll('[data-problem-tab]')];
      const panels = [...component.querySelectorAll('[data-problem-panel]')];
      if (!tabs.length || tabs.length !== panels.length) return;

      const show = (index, announce = false) => {
        const active = (index + tabs.length) % tabs.length;
        tabs.forEach((tab, i) => {
          const selected = i === active;
          tab.classList.toggle('is-active', selected);
          tab.setAttribute('aria-selected', String(selected));
          tab.tabIndex = selected ? 0 : -1;
        });
        panels.forEach((panel, i) => {
          panel.hidden = i !== active;
          panel.classList.toggle('is-active', i === active);
        });
        if (announce && liveRegion()) liveRegion().textContent = panels[active].textContent.trim();
      };

      tabs.forEach((tab, index) => tab.addEventListener('click', () => show(index, true)));
      component.querySelector('.problem-tabs')?.addEventListener('keydown', (event) => {
        const active = tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
        let next = active;
        if (event.key === 'ArrowLeft') next = active - 1;
        else if (event.key === 'ArrowRight') next = active + 1;
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = tabs.length - 1;
        else return;
        event.preventDefault();
        show(next, true);
        tabs[(next + tabs.length) % tabs.length].focus();
      });
      show(0);
    });
  }

  function initGallery(root = document) {
    root.querySelectorAll('[data-gallery]:not([data-ready])').forEach((gallery) => {
      gallery.dataset.ready = 'true';
      const track = gallery.querySelector('[data-gallery-track]');
      const slides = [...gallery.querySelectorAll('[data-gallery-slide]')];
      const dots = [...gallery.querySelectorAll('[data-gallery-dot]')];
      const count = gallery.querySelector('[data-gallery-count]');
      if (!track || !slides.length) return;
      let active = 0;

      const slidePosition = (slide) => slide.offsetLeft - slides[0].offsetLeft;
      const nearestSlide = () => slides.reduce((nearest, slide, index) => {
        const distance = Math.abs(slidePosition(slide) - track.scrollLeft);
        const nearestDistance = Math.abs(slidePosition(slides[nearest]) - track.scrollLeft);
        return distance < nearestDistance ? index : nearest;
      }, 0);
      const update = () => {
        active = nearestSlide();
        dots.forEach((dot, i) => {
          dot.classList.toggle('is-active', i === active);
          if (i === active) dot.setAttribute('aria-current', 'true');
          else dot.removeAttribute('aria-current');
        });
        slides.forEach((slide, i) => slide.setAttribute('aria-hidden', String(i !== active)));
        if (count) count.textContent = `${active + 1} / ${slides.length}`;
      };
      const go = (index) => {
        active = (index + slides.length) % slides.length;
        track.scrollTo({ left: slidePosition(slides[active]), behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
        update();
      };
      let frame;
      track?.addEventListener('scroll', () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(update);
      }, { passive: true });
      gallery.querySelector('[data-gallery-prev]')?.addEventListener('click', () => go(active - 1));
      gallery.querySelector('[data-gallery-next]')?.addEventListener('click', () => go(active + 1));
      dots.forEach((dot, index) => dot.addEventListener('click', () => go(index)));
      window.addEventListener('resize', update, { passive: true });
      update();
    });
  }

  function initProduct(root = document) {
    root.querySelectorAll('[data-product-root]:not([data-ready])').forEach((product) => {
      product.dataset.ready = 'true';
      const select = product.querySelector('[data-variant-select]');
      select?.addEventListener('change', () => {
        const option = select.selectedOptions[0];
        const price = product.querySelector('[data-product-price]');
        let compare = product.querySelector('[data-product-compare]');
        if (price) price.textContent = option.dataset.price || '';
        const mobilePrice = product.querySelector('[data-mobile-price]');
        if (mobilePrice) mobilePrice.textContent = option.dataset.price || '';
        if (option.dataset.compare) {
          if (!compare) {
            compare = document.createElement('s');
            compare.className = 'price-compare';
            compare.dataset.productCompare = '';
            product.querySelector('[data-price-wrap]')?.append(compare);
          }
          compare.textContent = option.dataset.compare;
          compare.hidden = false;
        } else if (compare) {
          compare.hidden = true;
        }
      });

      product.querySelectorAll('.quantity-input').forEach((quantity) => {
        const input = quantity.querySelector('input');
        quantity.querySelector('[data-qty-minus]')?.addEventListener('click', () => {
          input.value = Math.max(Number(input.min || 1), Number(input.value || 1) - 1);
        });
        quantity.querySelector('[data-qty-plus]')?.addEventListener('click', () => {
          input.value = Number(input.value || 1) + 1;
        });
      });

      const buyBar = product.querySelector('[data-mobile-buy-bar]');
      const productForm = product.querySelector('.product-form');
      const footer = document.querySelector('.footer');
      if (buyBar && productForm) {
        let frame;
        const updateBuyBar = () => {
          const isMobile = window.matchMedia('(max-width: 720px)').matches;
          const formHasPassed = productForm.getBoundingClientRect().bottom < 0;
          const footerIsClear = !footer || footer.getBoundingClientRect().top > window.innerHeight + 24;
          const visible = isMobile && formHasPassed && footerIsClear;
          buyBar.classList.toggle('is-visible', visible);
          buyBar.setAttribute('aria-hidden', String(!visible));
          frame = null;
        };
        const requestBuyBarUpdate = () => {
          if (!frame) frame = requestAnimationFrame(updateBuyBar);
        };
        window.addEventListener('scroll', requestBuyBarUpdate, { passive: true });
        window.addEventListener('resize', requestBuyBarUpdate, { passive: true });
        updateBuyBar();
      }
    });
  }

  function initCartDrawer() {
    const shell = document.querySelector('[data-cart-drawer]');
    if (!shell || shell.dataset.ready) return;
    shell.dataset.ready = 'true';
    const drawer = shell.querySelector('.cart-drawer');
    const body = shell.querySelector('[data-cart-drawer-body]');
    const footer = shell.querySelector('[data-cart-drawer-footer]');
    const total = shell.querySelector('[data-cart-drawer-total]');
    const closeButton = shell.querySelector('.cart-drawer-close');
    const fallbackImage = shell.dataset.productImage;
    let lastFocused;

    const escapeHTML = (value = '') => String(value).replace(/[&<>"']/g, (character) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;'
    })[character]);
    const formatMoney = (amount, currency = 'USD') => {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency }).format(amount / 100);
      } catch (_) {
        return `$${(amount / 100).toFixed(2)}`;
      }
    };
    const updateCount = (count) => {
      document.querySelectorAll('[data-cart-count]').forEach((element) => {
        element.textContent = count;
        element.classList.toggle('is-empty', count === 0);
      });
      document.querySelectorAll('[data-cart-drawer-trigger]').forEach((element) => {
        element.setAttribute('aria-label', `View cart with ${count} ${count === 1 ? 'item' : 'items'}`);
      });
    };
    const renderCart = (cart) => {
      updateCount(cart.item_count);
      footer.hidden = !cart.items.length;
      if (!cart.items.length) {
        body.innerHTML = '<div class="cart-drawer-empty"><h3>Your cart is empty.</h3><button type="button" data-cart-drawer-close>CONTINUE SHOPPING</button></div>';
        return;
      }
      body.innerHTML = `<div class="cart-drawer-items">${cart.items.map((item) => {
        const image = item.handle === 'rhia-skin-scanner' ? fallbackImage : (item.featured_image?.url || item.image || '');
        const variant = item.variant_title && item.variant_title !== 'Default Title' ? `<p>${escapeHTML(item.variant_title)}</p>` : '';
        return `<article class="cart-drawer-item">
          <a class="cart-drawer-item__image" href="${escapeHTML(item.url)}"><img src="${escapeHTML(image)}" alt="${escapeHTML(item.product_title)}" width="180" height="180"></a>
          <div class="cart-drawer-item__details">
            <h3><a href="${escapeHTML(item.url)}">${escapeHTML(item.product_title)}</a></h3>
            ${variant}
            <strong>${formatMoney(item.final_line_price, cart.currency)}</strong>
            <div class="cart-drawer-item__actions">
              <div class="cart-drawer-quantity" aria-label="Quantity for ${escapeHTML(item.product_title)}">
                <button type="button" data-cart-change data-key="${escapeHTML(item.key)}" data-quantity="${Math.max(0, item.quantity - 1)}" aria-label="Decrease quantity">−</button>
                <span>${item.quantity}</span>
                <button type="button" data-cart-change data-key="${escapeHTML(item.key)}" data-quantity="${item.quantity + 1}" aria-label="Increase quantity">+</button>
              </div>
              <button class="cart-drawer-remove" type="button" data-cart-change data-key="${escapeHTML(item.key)}" data-quantity="0">Remove</button>
            </div>
          </div>
        </article>`;
      }).join('')}</div>`;
      total.textContent = formatMoney(cart.total_price, cart.currency);
    };
    const refreshCart = async () => {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Unable to load your cart.');
      const cart = await response.json();
      renderCart(cart);
      return cart;
    };
    const openDrawer = async (refresh = true) => {
      lastFocused = document.activeElement;
      shell.classList.add('is-open');
      shell.setAttribute('aria-hidden', 'false');
      document.body.classList.add('cart-drawer-open');
      closeButton?.focus({ preventScroll: true });
      if (refresh) {
        try { await refreshCart(); }
        catch (error) { body.innerHTML = `<p class="cart-drawer-error">${escapeHTML(error.message)}</p>`; }
      }
    };
    const closeDrawer = () => {
      shell.classList.remove('is-open');
      shell.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('cart-drawer-open');
      lastFocused?.focus?.({ preventScroll: true });
    };

    document.addEventListener('click', async (event) => {
      const trigger = event.target.closest('[data-cart-drawer-trigger]');
      if (trigger) {
        event.preventDefault();
        openDrawer();
        return;
      }
      if (event.target.closest('[data-cart-drawer-close]')) {
        closeDrawer();
        return;
      }
      const changeButton = event.target.closest('[data-cart-change]');
      if (!changeButton || !shell.contains(changeButton)) return;
      changeButton.disabled = true;
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: changeButton.dataset.key, quantity: Number(changeButton.dataset.quantity) })
        });
        const cart = await response.json();
        if (!response.ok) throw new Error(cart.description || 'Unable to update your cart.');
        renderCart(cart);
      } catch (error) {
        const region = liveRegion();
        if (region) region.textContent = error.message;
        changeButton.disabled = false;
      }
    });
    document.addEventListener('submit', async (event) => {
      const form = event.target.closest('.product-form');
      if (!form) return;
      event.preventDefault();
      const submitter = event.submitter;
      const originalLabel = submitter?.textContent;
      if (submitter) {
        submitter.disabled = true;
        submitter.textContent = 'ADDING…';
      }
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form)
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || 'Unable to add Rhia to your cart.');
        await refreshCart();
        await openDrawer(false);
        const region = liveRegion();
        if (region) region.textContent = `${result.product_title || 'Rhia'} added to your cart.`;
      } catch (error) {
        const region = liveRegion();
        if (region) region.textContent = error.message;
      } finally {
        if (submitter) {
          submitter.disabled = false;
          submitter.textContent = originalLabel;
        }
      }
    });
    document.addEventListener('keydown', (event) => {
      if (!shell.classList.contains('is-open')) return;
      if (event.key === 'Escape') closeDrawer();
      if (event.key !== 'Tab') return;
      const focusable = [...drawer.querySelectorAll('a[href], button:not([disabled])')];
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    });
    refreshCart().catch(() => {});
  }

  function initCustomerForms() {
    const login = document.querySelector('[data-login-form]');
    const recover = document.querySelector('[data-recover-form]');
    document.querySelector('[data-recover-open]')?.addEventListener('click', () => {
      login.hidden = true;
      recover.hidden = false;
      history.replaceState(null, '', '#recover');
    });
    document.querySelector('[data-recover-cancel]')?.addEventListener('click', () => {
      login.hidden = false;
      recover.hidden = true;
      history.replaceState(null, '', location.pathname);
    });
    if (location.hash === '#recover' && login && recover) {
      login.hidden = true;
      recover.hidden = false;
    }

    const addressForm = document.querySelector('[data-address-form]');
    document.querySelector('[data-address-new]')?.addEventListener('click', () => {
      addressForm.hidden = false;
      addressForm.scrollIntoView({ behavior: 'smooth' });
    });
    document.querySelector('[data-address-cancel]')?.addEventListener('click', () => { addressForm.hidden = true; });
  }

  function init(root = document) {
    initMenu();
    initReveal(root);
    initHow(root);
    initProblem(root);
    initGallery(root);
    initProduct(root);
  }

  document.addEventListener('DOMContentLoaded', () => {
    initPageEntrance();
    init();
    initScrollFlow();
    initCartDrawer();
    initCustomerForms();
  });
  window.addEventListener('pageshow', () => document.body.classList.add('is-page-ready'));
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
